import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import type { Observable } from 'rxjs';

/**
 * Shape returned by both the data-plane and control-plane
 * `/api/v1/settings/auth/config` endpoints.
 *
 * Typed loosely so the same service can be consumed from the SmartCapacity
 * tenant UI (simple payload) and from the control-plane UI (wrapped in
 * `{ data }`). Each consumer narrows the response shape as needed.
 */
export interface MaskedAuthConfig {
  secret?: string;
  emailAndPassword?: {
    enabled?: boolean;
    requireEmailVerification?: boolean;
    minPasswordLength?: number;
    maxPasswordLength?: number;
  };
  socialProviders?: Record<
    string,
    {
      enabled?: boolean;
      clientId?: string;
      clientSecret?: string | null;
      tenantId?: string;
      allowedDomains?: string[];
      hasClientSecret?: boolean;
    } | null
  >;
}

/** Partial update payload for the auth config. */
export type UpdateAuthConfigPayload = Record<string, unknown>;

/** Envelope used by the control-plane variant. */
export interface AuthConfigEnvelope<T = MaskedAuthConfig> {
  data: T;
}

/**
 * Service for reading and updating the auth settings (`/api/v1/settings/auth/config`).
 *
 * Shared by both `AuthSettingsComponent` (tenant UI) and
 * `AdminAuthSettingsComponent` (control-plane UI). The two callers differ
 * only in the response envelope shape — the service exposes helpers for each.
 */
@Injectable({ providedIn: 'root' })
export class AuthSettingsService {
  private readonly http = inject(HttpClient);
  private readonly configPath = '/api/v1/settings/auth/config';

  /** Fetch the auth config (flat response). */
  getConfig<T = MaskedAuthConfig>(): Observable<T> {
    return this.http.get<T>(this.configPath);
  }

  /** Fetch the auth config as an envelope (`{ data }`) — used by control-plane UI. */
  getConfigEnvelope<T = MaskedAuthConfig>(): Observable<AuthConfigEnvelope<T>> {
    return this.http.get<AuthConfigEnvelope<T>>(this.configPath);
  }

  /** Partial-update the auth config (flat response). */
  patchConfig<T = MaskedAuthConfig>(payload: UpdateAuthConfigPayload): Observable<T> {
    return this.http.patch<T>(this.configPath, payload);
  }

  /** Partial-update the auth config — envelope variant used by control-plane UI. */
  patchConfigEnvelope<T = MaskedAuthConfig>(payload: UpdateAuthConfigPayload): Observable<AuthConfigEnvelope<T>> {
    return this.http.patch<AuthConfigEnvelope<T>>(this.configPath, payload);
  }
}
